<template>
    <div class="content-wrapper">
        <div class="row align-items-center">
            <!-- Left Column -->
            <div class="col-md-12 col-lg-6">
                <div class="d-flex align-items-center">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb p-0 d-flex align-items-center mb-primary">
                            <li class="breadcrumb-item page-header d-flex align-items-center">
                                <h4 class="mb-0">{{ $t('polls.poll_details') }}</h4>
                            </li>
                            <template v-if="$can('view_polls')">
                                <li class="ml-2">|</li>
                                <li>
                                    <a :href="urlGenerator(pollWebUrl)"
                                       class="btn btn-link text-primary pl-2">
                                        {{ $t('polls.back_to_all_poll') }}
                                    </a>
                                </li>
                            </template>
                        </ol>
                    </nav>
                </div>
            </div>

            <!-- Right Column -->
            <div class="col-md-12 col-lg-6">
                <div class="d-flex justify-content-lg-end align-items-center gap-2 mb-primary">
                    <button type="button"
                            class="btn btn-success mr-2"
                            v-if="poll.questions && poll.questions.length > 0"
                            @click="startPoll()">
                        {{ $t('polls.start_poll') }}
                    </button>
                    <div class="dropdown" v-if="$can('update_poll') || $can('delete_poll')">
                        <button type="button"
                                class="btn btn-success dropdown-toggle"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false">
                            {{ $t('action') }}
                            <app-icon name="chevron-down"/>
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <a href="#" class="dropdown-item" v-if="$can('update_poll')" @click="editPoll">
                                {{ $t('edit') }}
                            </a>
                            <a href="#" class="dropdown-item" v-if="$can('delete_poll')" @click.prevent="deletePoll">
                                {{ $t('delete') }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <div class="poll-profile mb-primary">
            <div class="card card-with-shadow py-5 border-0" style="min-height: 220px;">
                <app-overlay-loader v-if="loader"/>
                <div v-else class="row">
                    <!-- Left Section -->
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-5">
                        <div class="media border-right px-5 poll-header-media align-items-center">
                            <div class="media-body poll-info-header">
                                <p class="text-muted mb-0">{{ $t('title') }}</p>
                                <p>{{ poll.title }}</p>
                            </div>
                        </div>

                        <div class="media border-right px-5 poll-header-media align-items-center">
                            <div class="media-body poll-info-header">
                                <p class="text-muted mb-0 mt-2">{{ $t('description') }}</p>
                                <p>{{ poll.description }}</p>
                            </div>
                        </div>

                        <div class="media border-right px-5 poll-header-media align-items-center">
                            <div class="media-body poll-info-header">
                                <p class="text-muted mb-0 mt-2">{{ $t('status') }}</p>
                                <p v-if="poll.status_id" :class="['badge', 'badge-pill', `badge-${poll.status.class}`]">
                                    {{ poll.status.translated_name }}
                                </p>
                                <p v-else>{{ $t('not_added_yet') }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Right Section -->
                    <div class="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-7">
                        <div
                            class="poll-details px-5 px-sm-5 px-md-5 px-lg-0 px-xl-0 mt-5 mt-sm-5 mt-md-0 mt-lg-0 mt-xl-0">
                            <div class="row">
                                <!-- Left Column of Details -->
                                <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6">
                                    <div class="border-right custom">
                                        <div class="media mb-4 mb-xl-5">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="user"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('created_by') }}</p>
                                                <p v-if="poll.created_by">{{ poll.creator.full_name }}</p>
                                                <p v-else>{{ $t('not_added_yet') }}</p>
                                            </div>
                                        </div>

                                        <div class="media mb-4 mb-xl-5">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="calendar"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('created_at') }}</p>
                                                <p>{{ formattedDate(poll.created_at) }}</p>
                                            </div>
                                        </div>

                                        <div class="media mb-4 mb-xl-5">
                                            <div class="align-self-center mr-3">
                                                <app-icon name="calendar"/>
                                            </div>
                                            <div class="media-body">
                                                <p class="text-muted mb-0">{{ $t('updated_at') }}</p>
                                                <p>{{ formattedDate(poll.updated_at) }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6">
                                    <div class="media mb-4 mb-xl-5">
                                        <div class="align-self-center mr-3">
                                            <app-icon name="briefcase"/>
                                        </div>
                                        <div class="media-body">
                                            <p class="text-muted mb-0">{{ $t('departments') }}</p>
                                            <div v-if="poll.departments && poll.departments.length">
                                                <p class="mb-0" v-for="(department, index) in poll.departments" :key="index">
                                                    {{ department.name }}
                                                </p>
                                            </div>
                                            <p class="mb-0" v-else>{{ $t('not_added_yet') }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <app-poll-modal
            v-if="isPollModalActive"
            v-model="isPollModalActive"
            :selected-url="updateUrl"
        />

        <app-confirmation-modal
            :message="promptSubtitle"
            :modal-class="modalClass"
            :icon="promptIcon"
            v-if="confirmationModalActive"
            modal-id="app-confirmation-modal"
            @confirmed="triggerConfirm"
            @cancelled="cancelled"
        />
    </div>
</template>

<script>
import {axiosDelete, urlGenerator} from '../../../../../common/Helper/AxiosHelper';
import {FormMixin} from "../../../../../core/mixins/form/FormMixin";
import pollCreateEditModal from "../../Pms/Poll/PollCreateEditModal"; // Adjust the path to match your file structure
import {formatDateTimeToLocalDate, formattedDate} from "../../../../../common/Helper/Support/DateTimeHelper";
import {mapState} from "vuex";
import {POLL, POLL_WEB} from "../../../../Config/ApiUrl"; // Adjust the API URLs accordingly

export default {
    name: "pollDetails",
    mixins: [FormMixin],
    components: {pollCreateEditModal},
    props: {
        pollId: {}
    },
    data() {
        return {
            poll: {},
            pollWebUrl: POLL_WEB,
            urlGenerator,
            formatDateTimeToLocalDate,
            viewPollModal: false,
            isPollModalActive: false,
            confirmationModalActive: false,
            isPollNoteModalActive: false,
            deleteUrl: '',
            promptAction: '',
            updateUrl: '',
            modalClass: '',
            promptIcon: '',
            promptSubtitle: '',
            formattedDate
        };
    },
    mounted() {
        this.$store.dispatch("getPollDetails", this.pollId)
            .then(() => {
                // Ensure the poll data is set after fetching
                this.poll = this.pollDetails;
            })
            .catch(error => {
                console.error("Error fetching poll details:", error);
            });
    },
    methods: {
        startPoll() {
            window.location.href = urlGenerator(`${POLL_WEB}/${this.pollId}/form`) // Redirect to the generated URL
        },
        cancelled() {
            this.confirmationModalActive = false;
        },

        editPoll() {
            this.updateUrl = `${POLL}/${this.pollId}`;
            this.isPollModalActive = true;
        },

        // ✅ Delete poll functionality
        deletePoll() {
            this.confirmationModalActive = true;
            this.deleteUrl = `${POLL}/${this.pollId}`;
            this.promptAction = 'delete_poll';
            this.promptTitle = this.$t('are_you_sure');
            this.promptMessage = this.$t('poll.you_are_going_to_remove_an_poll_from_list');
        },

        // ✅ Confirmation Modal Close
        closeConfirmation() {
            $("#app-confirmation-modal").modal('hide');
            $(".modal-backdrop").remove();
            this.loading = false;
            this.confirmationModalActive = false;
            // ✅ Redirect using urlGenerator
            window.location.href = urlGenerator(`${POLL_WEB}`);
        },

        // ✅ Handles confirm button in modal
        triggerConfirm() {
            if (this.promptAction === 'delete_poll') {
                this.removePoll(this.deleteUrl);
            }
        },

        // ✅ Delete request
        removePoll(url) {
            this.loading = true;

            axiosDelete(url)
                .then(({data}) => {
                    this.confirmationModalActive = false;
                    this.toastAndReload(data.message, 'poll-table');
                })
                .catch(({response}) => {
                    this.toastException(response.data);
                    this.confirmationModalActive = false;
                })
                .finally(() => this.closeConfirmation());
        }
    },
    computed: {
        ...mapState({
            pollDetails: state => state.poll.poll,
            loader: state => state.loading,
        }),
    },
    watch: {
        pollDetails: {
            handler(poll) {
                if (poll) {
                    this.poll = poll;
                    document.title = poll.title;
                }
            },
            deep: true
        }
    }
};
</script>
